import { useState } from 'react'
import { Check, Copy, Loader2, MessageCircle, X } from 'lucide-react'
import { API_ROUTES } from '@/config/api-routes'
import { salvarCanal, type CanalConexao, type ProvedorCanal } from '@/services/atendimento'

export default function EditarConexaoModal({
  workspaceId, canal, onClose, onSaved,
}: {
  workspaceId: string
  canal: CanalConexao | null
  onClose: () => void
  onSaved: (aviso: string) => void
}) {
  const [provedor, setProvedor] = useState<ProvedorCanal>(canal?.provedor ?? 'simulado')
  const [nome, setNome] = useState(canal?.nome ?? 'WhatsApp do consultório')
  const [telefone, setTelefone] = useState(canal?.telefone_exibicao ?? '')
  const [phoneNumberId, setPhoneNumberId] = useState(canal?.phone_number_id ?? '')
  const [verifyToken, setVerifyToken] = useState(canal?.verify_token ?? crypto.randomUUID().replace(/-/g, ''))
  const [busy, setBusy] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [copiado, setCopiado] = useState(false)

  const urlWebhook = typeof window !== 'undefined'
    ? `${window.location.origin}${API_ROUTES.whatsapp.webhook}`
    : ''

  async function salvar(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true); setErro(null)
    try {
      await salvarCanal({
        workspaceId,
        id: canal?.id,
        provedor, nome,
        telefoneExibicao: telefone || null,
        phoneNumberId: provedor === 'cloud_api' ? phoneNumberId : null,
        verifyToken,
        provedorAnterior: canal?.provedor,
      })
      onSaved(
        provedor === 'simulado'
          ? 'Conexão simulada pronta — já dá para atender pelo módulo Atendimento.'
          : provedor === 'baileys'
            ? 'Conexão salva. O worker sobe na VM em ~15s e o QR fica disponível pelo botão de QR code.'
            : 'Conexão salva. Falta cadastrar o token de acesso (veja as instruções abaixo) e apontar o webhook na Meta.',
      )
      onClose()
    } catch {
      setErro('Não foi possível salvar (só o médico titular administra a conexão).')
    } finally { setBusy(false) }
  }

  return (
    <div
      role="dialog" aria-modal="true" aria-label={canal ? 'Editar conexão' : 'Nova conexão'}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15, 23, 32, .45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 16, overflowY: 'auto',
      }}
      onClick={onClose}
    >
      <form
        className="card"
        style={{ width: '100%', maxWidth: 480, padding: 20, display: 'flex', flexDirection: 'column', gap: 12, maxHeight: '90vh', overflowY: 'auto' }}
        onClick={e => e.stopPropagation()}
        onSubmit={salvar}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <strong style={{ fontSize: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <MessageCircle size={17} aria-hidden style={{ color: 'var(--primary)' }} />
            {canal ? 'Editar conexão' : 'Nova conexão'}
          </strong>
          <button
            type="button" className="btn-secondary" aria-label="Fechar"
            style={{ minHeight: 32, width: 32, padding: 0, justifyContent: 'center' }}
            onClick={onClose}
          >
            <X size={15} aria-hidden />
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
          <div>
            <label className="field-label" htmlFor="wa-prov">Provedor</label>
            <select id="wa-prov" className="field-input" value={provedor}
              onChange={e => setProvedor(e.target.value as ProvedorCanal)}>
              <option value="simulado">Simulado (testes, sem custo)</option>
              <option value="cloud_api">WhatsApp Cloud API (oficial Meta)</option>
              <option value="baileys">WhatsApp Web por QR (não-oficial)</option>
            </select>
          </div>
          <div>
            <label className="field-label" htmlFor="wa-nome">Nome do canal</label>
            <input id="wa-nome" className="field-input" value={nome} onChange={e => setNome(e.target.value)} />
          </div>
          <div>
            <label className="field-label" htmlFor="wa-tel">Telefone exibido</label>
            <input id="wa-tel" className="field-input" value={telefone} onChange={e => setTelefone(e.target.value)} placeholder="(48) 3000-0000" />
          </div>
        </div>

        {provedor === 'cloud_api' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
            <div>
              <label className="field-label" htmlFor="wa-pni">Phone Number ID</label>
              <input id="wa-pni" className="field-input" value={phoneNumberId} onChange={e => setPhoneNumberId(e.target.value)} placeholder="ID do número na Meta" />
            </div>
            <div>
              <label className="field-label" htmlFor="wa-vt">Verify token do webhook</label>
              <input id="wa-vt" className="field-input" value={verifyToken} onChange={e => setVerifyToken(e.target.value)} />
            </div>
          </div>
        )}

        {erro && <div className="field-error" role="alert">{erro}</div>}

        <button type="submit" className="btn-primary" style={{ minHeight: 40 }} disabled={busy}>
          {busy && <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} aria-hidden />}
          Salvar conexão
        </button>
        <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>

        {provedor === 'cloud_api' && (
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, fontSize: 13.5, color: 'var(--text-secondary)' }}>
            <strong style={{ color: 'var(--text)' }}>Para ativar o número oficial:</strong>
            <ol style={{ margin: '8px 0 0', paddingLeft: 18, lineHeight: 1.7 }}>
              <li>No painel da Meta, aponte o webhook para:
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4, flexWrap: 'wrap' }}>
                  <code style={{ fontSize: 12, background: 'var(--bg)', padding: '6px 8px', borderRadius: 6, wordBreak: 'break-all' }}>{urlWebhook}</code>
                  <button type="button" className="btn-secondary" style={{ minHeight: 30, padding: '2px 8px', fontSize: 12 }}
                    onClick={async () => { await navigator.clipboard.writeText(urlWebhook); setCopiado(true); setTimeout(() => setCopiado(false), 2000) }}>
                    {copiado ? <Check size={12} aria-hidden /> : <Copy size={12} aria-hidden />}
                  </button>
                </div>
              </li>
              <li>Use o <em>verify token</em> acima na verificação e assine o campo <code>messages</code>.</li>
              <li>Grave o <strong>access token</strong> e o <strong>app secret</strong> pelo comando abaixo (eles nunca passam pelo navegador):</li>
            </ol>
            <pre style={{ background: 'var(--bg)', padding: 10, borderRadius: 8, fontSize: 11.5, overflowX: 'auto', marginTop: 8 }}>
{`-- No SQL Editor do Supabase (canal_credenciais não é legível pelo app):
insert into canal_credenciais (canal_conexao_id, access_token, app_secret)
values ('${canal?.id ?? '<id-da-conexao>'}', 'SEU_ACCESS_TOKEN', 'SEU_APP_SECRET')
on conflict (canal_conexao_id) do update
  set access_token = excluded.access_token,
      app_secret   = excluded.app_secret,
      atualizado_em = now();`}
            </pre>
            <p style={{ margin: '8px 0 0' }}>
              O servidor também precisa de <code>SUPABASE_SERVICE_ROLE</code> no <code>.env</code> para enviar e receber.
            </p>
          </div>
        )}
      </form>
    </div>
  )
}
